import React, { useState, useMemo } from 'react';
import CalcShell from '../CalcShell';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

function SectionShell({ title, definition, example, children }) {
  return (
    <div className="mt-6 first:mt-0">
      <h3 className="text-lg font-bold text-slate-900">{title}</h3>
      <div className="text-sm text-slate-600 mt-1">
        <div><em>definition:</em> {definition}</div>
        <div><em>example:</em> {example}</div>
      </div>
      <div className="mt-3 bg-slate-100 rounded-xl p-4">
        {children}
      </div>
    </div>
  );
}

function InputRow({ label, value, onChange, step = 'any' }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] items-center gap-3 mb-3">
      <label className="text-sm text-slate-700">{label}</label>
      <input type="number" step={step} value={value} onChange={onChange} className="px-3 py-2 rounded border border-slate-300 bg-white w-full md:max-w-[220px]" />
    </div>
  );
}

function CalcButtons({ onCalc, onClear, testId }) {
  return (
    <div className="flex gap-2 mt-2">
      <button onClick={onCalc} data-testid={`${testId}-calc`} className="inline-flex items-center gap-2 px-5 py-2 rounded bg-[#4c7b25] hover:bg-[#4c7b25] text-white font-semibold text-sm shadow-sm">
        <span className="inline-block w-0 h-0 border-l-[7px] border-l-white border-y-[5px] border-y-transparent"></span> Calculate
      </button>
      <button onClick={onClear} data-testid={`${testId}-clear`} className="px-5 py-2 rounded bg-slate-200 hover:bg-slate-300 text-slate-800 font-medium text-sm">Clear</button>
    </div>
  );
}

function ResultBlock({ terms, sum, chartLabel }) {
  if (!terms || terms.length === 0) return null;
  const data = terms.map((t, i) => ({ n: i + 1, value: typeof t === 'number' ? t : Number(t) }));
  return (
    <div className="mt-4 bg-white rounded-xl p-4 border border-slate-200">
      <div className="flex items-center gap-2 bg-[#4c7b25] text-white -mx-4 -mt-4 mb-3 px-4 py-2 rounded-t-xl">
        <span className="text-sm font-semibold">Result</span>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <div className="text-xs text-slate-500 mb-1">Sequence (first {terms.length} terms):</div>
          <div className="font-mono text-sm break-all bg-slate-50 rounded p-3 max-h-40 overflow-auto">
            {terms.map((t, i) => (
              <span key={i}>
                {String(t)}{i < terms.length - 1 ? ', ' : ''}
              </span>
            ))}
          </div>
          <table className="w-full text-sm mt-3 border border-slate-200">
            <thead className="bg-slate-100"><tr><th className="px-3 py-1.5 text-left">n</th><th className="px-3 py-1.5 text-right">aₙ</th></tr></thead>
            <tbody>
              {terms.slice(0, 20).map((t, i) => (
                <tr key={i} className={i % 2 ? 'bg-slate-50' : ''}>
                  <td className="px-3 py-1.5">{i + 1}</td>
                  <td className="px-3 py-1.5 text-right font-mono">{String(t)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-3 text-sm">
            <span className="text-slate-600">Sum of terms Sₙ = </span>
            <span className="font-bold text-blue-700">{String(sum)}</span>
          </div>
        </div>
        <div>
          <div className="text-xs text-slate-500 mb-1">{chartLabel || 'Growth chart'}</div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="n" stroke="#64748b" fontSize={11} />
              <YAxis stroke="#64748b" fontSize={11} />
              <Tooltip contentStyle={{ background: '#0f172a', border: 'none', borderRadius: 6, color: '#fff', fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="value" name="aₙ" stroke="#2563eb" strokeWidth={2.5} dot={{ r: 3, fill: '#2563eb' }} activeDot={{ r: 6 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function ArithmeticSection() {
  const [a, setA] = useState(2);
  const [d, setD] = useState(5);
  const [n, setN] = useState(20);
  const [result, setResult] = useState(null);
  const calc = () => {
    const N = Math.max(1, Math.min(1000, Math.floor(n)));
    const arr = []; let s = 0;
    for (let i = 0; i < N; i++) { const t = a + i * d; arr.push(t); s += t; }
    setResult({ terms: arr, sum: s });
  };
  const clear = () => { setA(0); setD(0); setN(0); setResult(null); };
  return (
    <SectionShell title="Arithmetic Sequence Calculator" definition={<span>aₙ = a₁ + f × (n − 1)</span>} example="1, 3, 5, 7, 9, 11, 13, …">
      <InputRow label="the first number" value={a} onChange={e => setA(+e.target.value || 0)} />
      <InputRow label="common difference (f)" value={d} onChange={e => setD(+e.target.value || 0)} />
      <InputRow label="the nth number to obtain" value={n} onChange={e => setN(+e.target.value || 0)} step="1" />
      <CalcButtons testId="arith" onCalc={calc} onClear={clear} />
      {result && <ResultBlock terms={result.terms} sum={result.sum} chartLabel="Arithmetic sequence growth" />}
    </SectionShell>
  );
}

function GeometricSection() {
  const [a, setA] = useState(2);
  const [r, setR] = useState(2);
  const [n, setN] = useState(12);
  const [result, setResult] = useState(null);
  const calc = () => {
    const N = Math.max(1, Math.min(60, Math.floor(n)));
    const arr = []; let s = 0;
    for (let i = 0; i < N; i++) { const t = a * Math.pow(r, i); arr.push(t); s += t; }
    setResult({ terms: arr, sum: s });
  };
  const clear = () => { setA(0); setR(0); setN(0); setResult(null); };
  return (
    <SectionShell title="Geometric Sequence Calculator" definition={<span>aₙ = a × r^(n−1)</span>} example="1, 2, 4, 8, 16, 32, 64, 128, …">
      <InputRow label="the first number" value={a} onChange={e => setA(+e.target.value || 0)} />
      <InputRow label="common ratio (r)" value={r} onChange={e => setR(+e.target.value || 0)} />
      <InputRow label="the nth number to obtain" value={n} onChange={e => setN(+e.target.value || 0)} step="1" />
      <CalcButtons testId="geo" onCalc={calc} onClear={clear} />
      {result && <ResultBlock terms={result.terms} sum={result.sum} chartLabel="Geometric sequence growth" />}
    </SectionShell>
  );
}

function FibonacciSection() {
  const [n, setN] = useState(10);
  const [result, setResult] = useState(null);
  const calc = () => {
    const N = Math.max(1, Math.min(80, Math.floor(n)));
    const arr = []; let a = 0n, b = 1n;
    for (let i = 0; i < N; i++) { arr.push(a.toString()); const c = a + b; a = b; b = c; }
    const sum = arr.reduce((s, v) => s + BigInt(v), 0n).toString();
    setResult({ terms: arr, sum });
  };
  const clear = () => { setN(0); setResult(null); };
  return (
    <SectionShell title="Fibonacci Sequence Calculator" definition={<span>a₀ = 0; a₁ = 1; aₙ = aₙ₋₁ + aₙ₋₂</span>} example="0, 1, 1, 2, 3, 5, 8, 13, 21, 34, 55, …">
      <InputRow label="the nth number to obtain" value={n} onChange={e => setN(+e.target.value || 0)} step="1" />
      <CalcButtons testId="fib" onCalc={calc} onClear={clear} />
      {result && <ResultBlock terms={result.terms} sum={result.sum} chartLabel="Fibonacci sequence growth" />}
    </SectionShell>
  );
}

export default function NumberSequenceCalc() {
  return (
    <CalcShell look="cnet" title="Number Sequence Calculator" subtitle="Modify the values and click the Calculate button to use">
      <ArithmeticSection />
      <GeometricSection />
      <FibonacciSection />
    </CalcShell>
  );
}
